/** Spreadsheet views of a fast dataset: sort by any column or paper field, filter by values. */
import { cellText } from "./components/EvidencePanel";
import { designName, paperName } from "./cite";
import type { FastColumn, FastRow } from "./types";

/** The paper's own fields, sortable and filterable like columns. */
export const PAPER_FIELDS = [
  { key: "_evidence", label: "Evidence score", kind: "number" },
  { key: "_citations", label: "Citations", kind: "number" },
  { key: "_year", label: "Year", kind: "number" },
  { key: "_design", label: "Study type", kind: "category" },
  { key: "_journal", label: "Journal", kind: "category" },
  { key: "_paper", label: "Source name", kind: "text" },
] as const;

export type Kind = "number" | "bool" | "category" | "text" | "image";
export type Sort = { key: string; dir: "asc" | "desc"; smart?: { order: string[]; criterion: string } };
/** Per column or field: the values kept. */
export type Filters = Record<string, string[]>;

export const EMPTY = "(empty)";
/** A text column with this many values or fewer is filtered like a category. */
const FEW_VALUES = 20;

export const kindOf = (column: FastColumn): Kind =>
  column.value_type === "number" || column.value_type === "count"
    ? "number"
    : column.value_type === "yes_no"
      ? "bool"
      : column.value_type === "category"
        ? "category"
        : column.value_type === "image"
          ? "image"
          : "text";

export const fieldKind = (key: string, columns: FastColumn[]): Kind => {
  const field = PAPER_FIELDS.find((f) => f.key === key);
  if (field) return field.kind;
  const column = columns.find((c) => c.key === key);
  return column ? kindOf(column) : "text";
};

/** The first number in a value: 14.9 from "−14.9% (95% CI …)", 1961 from "1,961". */
const firstNumber = (text: string): number | null => {
  const m = text.replace(/[−–]/g, "-").match(/-?\d[\d,]*(?:\.\d+)?|-?\.\d+/);
  if (!m) return null;
  const n = Number(m[0].replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
};

/** A row's value for a column or paper field, as shown; null when it has none. */
export const shownValue = (row: FastRow, key: string): string | null => {
  const s = row.source;
  switch (key) {
    case "_evidence":
      return row.evidence_score == null ? null : String(row.evidence_score);
    case "_citations":
      return s.citations == null ? null : String(s.citations);
    case "_year":
      return s.year == null || s.year === "" ? null : String(s.year);
    case "_design":
      return designName(s.design) || null;
    case "_journal":
      return s.journal || null;
    case "_paper":
      return paperName(row) || null;
  }
  if (row.figures?.[key]) return "Has image";
  const cell = row.cells[key];
  if (!cell) return null;
  if (typeof cell.value === "boolean") return cell.value ? "Yes" : "No";
  return cellText(cell) || null;
};

const sortValue = (row: FastRow, key: string, kind: Kind, options: string[]): number | string | null => {
  if (key === "_evidence") return row.evidence_score ?? null;
  if (key === "_citations") return row.source.citations ?? null;
  const shown = shownValue(row, key);
  if (shown === null) return null;
  if (kind === "number") {
    const value = row.cells[key]?.value;
    return typeof value === "number" ? value : firstNumber(shown);
  }
  if (kind === "bool") return shown === "Yes" ? 1 : 0;
  if (kind === "category" && options.length) {
    const at = options.findIndex((o) => o.toLowerCase() === shown.toLowerCase());
    return at >= 0 ? at : options.length;
  }
  return shown.toLowerCase();
};

const text = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });

/** The rows to show: filtered, then sorted; rows without a value go last either way. */
export const view = (rows: FastRow[], columns: FastColumn[], sort: Sort | null, filters: Filters): FastRow[] => {
  const kept = rows.filter((row) =>
    Object.entries(filters).every(([key, allowed]) => allowed.includes(shownValue(row, key) ?? EMPTY)),
  );
  if (!sort) return kept;
  const sign = sort.dir === "asc" ? 1 : -1;
  if (sort.smart) {
    const at = new Map(sort.smart.order.map((k, i) => [k, i]));
    const place = (r: FastRow) => at.get(r.row_key) ?? Infinity;
    return [...kept].sort((a, b) => {
      const pa = place(a), pb = place(b);
      if (pa === Infinity || pb === Infinity) return pa === pb ? 0 : pa === Infinity ? 1 : -1;
      return -sign * (pa - pb);
    });
  }
  const kind = fieldKind(sort.key, columns);
  const options = columns.find((c) => c.key === sort.key)?.options ?? [];
  const values = new Map(kept.map((r) => [r.row_key, sortValue(r, sort.key, kind, options)]));
  return [...kept].sort((a, b) => {
    const va = values.get(a.row_key) ?? null, vb = values.get(b.row_key) ?? null;
    if (va === null || vb === null) return va === vb ? 0 : va === null ? 1 : -1;
    const d = typeof va === "number" && typeof vb === "number" ? va - vb : text.compare(String(va), String(vb));
    return sign * d;
  });
};

/** The values a column or field can be filtered by, most common first; none when there are too many to list. */
export const filterValues = (rows: FastRow[], key: string, kind: Kind): { value: string; count: number }[] => {
  if (kind === "number" || kind === "image") return [];
  const counts = new Map<string, number>();
  for (const row of rows) {
    const v = shownValue(row, key) ?? EMPTY;
    counts.set(v, (counts.get(v) ?? 0) + 1);
  }
  if (kind === "text" && counts.size > FEW_VALUES) return [];
  return [...counts].map(([value, count]) => ({ value, count })).sort((a, b) => b.count - a.count || text.compare(a.value, b.value));
};

/** Low to high / A to Z, in the words that suit the kind. */
export const sortWords = (kind: Kind): { asc: string; desc: string } =>
  kind === "number"
    ? { asc: "Low to high", desc: "High to low" }
    : kind === "bool"
      ? { asc: "No first", desc: "Yes first" }
      : kind === "image"
        ? { asc: "Image last", desc: "Image first" }
        : { asc: "A to Z", desc: "Z to A" };
